import React, { useEffect, useMemo, useState } from 'react';
import { useLang } from './LangContext';
import Paginator, { usePaginator } from './Paginator';

// ============================================================
// ContributorsAdmin
//   ky_thuat-only panel for managing the guest Contribute page.
//   Pick a member from the existing roster — their name / avatar / role
//   are pulled from /api/admin/members. The admin only edits:
//     - phân loại đóng góp (category)
//     - nội dung đóng góp (contributions)
//     - thứ tự hiển thị (order)
//     - đánh dấu nổi bật (highlight)
// ============================================================

const ROLE_LABEL = {
  ban_dieu_hanh: 'Ban Điều Hành',
  ky_thuat: 'Kỹ Thuật',
  thanh_vien: 'Thành Viên',
  ban_su_kien: 'Ban Sự Kiện',
  ban_dao_tao: 'Ban Đào Tạo'
};
const TITLE_LABEL = {
  founder: 'Founder',
  chu_nhiem: 'Chủ Nhiệm',
  pho_chu_nhiem: 'Phó Chủ Nhiệm',
  truong_ban_dao_tao: 'Trưởng Ban Đào Tạo',
  pho_ban_dao_tao: 'Phó Ban Đào Tạo',
  truong_ban_su_kien: 'Trưởng Ban Sự Kiện',
  pho_ban_su_kien: 'Phó Ban Sự Kiện'
};
function describeMember(u) {
  if (!u) return '';
  if (u.title && TITLE_LABEL[u.title]) return TITLE_LABEL[u.title];
  const roles = Array.isArray(u.role) ? u.role : (u.role ? [u.role] : []);
  return roles.map(r => ROLE_LABEL[r] || r).join(' · ');
}

const emptyForm = () => ({
  id: null,
  userId: '',
  category: '',
  contributions: '',   // textarea, one bullet per line
  order: 0,
  highlight: false
});

const ContributorsAdmin = ({ token }) => {
  const lang = useLang();
  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const authHeaders = { Authorization: `Bearer ${token}` };
  const show = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const load = async () => {
    setLoading(true);
    try {
      const [resC, resM] = await Promise.all([
        fetch('/api/contributors'),
        fetch('/api/admin/members', { headers: authHeaders })
      ]);
      const dataC = await resC.json();
      const dataM = await resM.json();
      if (!resC.ok) throw new Error(dataC.error || 'Không tải được danh sách đóng góp.');
      if (!resM.ok) throw new Error(dataM.error || 'Không tải được danh sách thành viên.');
      setList(dataC.contributors || []);
      setMembers(dataM.members || []);
    } catch (err) { show('error', err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const reset = () => { setForm(emptyForm()); setEditing(null); setShowForm(false); };

  const startEdit = (c) => {
    setEditing(c);
    setShowForm(true);
    setForm({
      id: c.id,
      userId: c.userId || '',
      category: c.category || '',
      contributions: Array.isArray(c.contributions) ? c.contributions.join('\n') : '',
      order: typeof c.order === 'number' ? c.order : 0,
      highlight: !!c.highlight
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.userId) return show('error', 'Hãy chọn một thành viên.');
    const body = {
      userId: parseInt(form.userId),
      category: form.category.trim(),
      contributions: form.contributions.split('\n').map(s => s.trim()).filter(Boolean),
      order: parseInt(form.order) || 0,
      highlight: !!form.highlight
    };
    try {
      const url = editing ? `/api/contributors/${editing.id}` : '/api/contributors';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', editing ? 'Đã cập nhật.' : 'Đã thêm đóng góp mới.');
      reset();
      load();
    } catch (err) { show('error', err.message); }
  };

  const del = async (c) => {
    if (!window.confirm(lang.CONTRIB_DELETE_CONFIRM || `Xoá "${c.name}" khỏi danh sách đóng góp?`)) return;
    try {
      const res = await fetch(`/api/contributors/${c.id}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', 'Đã xoá.');
      load();
    } catch (err) { show('error', err.message); }
  };

  // Member picker — exclude users already in the list (unless we're editing them).
  const usedUserIds = useMemo(() => {
    const s = new Set(list.map(c => c.userId).filter(Boolean));
    if (editing && editing.userId) s.delete(editing.userId);
    return s;
  }, [list, editing]);

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return members
      .filter(m => !usedUserIds.has(m.id))
      .filter(m => {
        if (!q) return true;
        const hay = `${m.fullName || ''} ${m.username || ''} ${m.email || ''}`.toLowerCase();
        return hay.includes(q);
      });
  }, [members, memberQuery, usedUserIds]);

  const selectedMember = members.find(m => m.id === parseInt(form.userId));

  // Sorted list (memoized) so the paginator slices a stable order — order
  // first, then alphabetical by name as a tiebreaker.
  const sortedList = useMemo(
    () => list.slice().sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)),
    [list]
  );
  const listPg = usePaginator(sortedList, 10);

  return (
    <div className="contributors-admin">
      <div className="contributors-admin-head">
        <div>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>{lang.CONTRIB_ADMIN_TITLE || 'Quản Lý Đóng Góp'}</h2>
        </div>
      </div>

      {msg && <div className={`training-msg ${msg.type}`}>{msg.text}</div>}

      {!showForm && !editing && (
        <div style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm()); }}>
            + {lang.CONTRIB_ADD_NEW || 'Thêm đóng góp mới'}
          </button>
        </div>
      )}

      {(showForm || editing) && (
      <form onSubmit={submit} className="training-form" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>{editing ? `Chỉnh sửa: ${editing.name}` : (lang.CONTRIB_ADD_NEW || 'Thêm đóng góp mới')}</h3>

        <div className="form-group" style={{ marginBottom: 14 }}>
              <label>{lang.CONTRIB_MEMBER_REQUIRED || 'Thành viên *'}</label>
          {selectedMember ? (
            <div className="contrib-member-chip">
              {selectedMember.avatar
                ? <img src={`/uploads/profiles/${selectedMember.avatar}`} alt="" />
                : <div className="contrib-member-chip-avatar-fallback">
                    {(selectedMember.fullName || selectedMember.username || '?').charAt(0).toUpperCase()}
                  </div>}
              <div className="contrib-member-chip-info">
                <strong>{selectedMember.fullName || selectedMember.username}</strong>
                <span>{describeMember(selectedMember)}</span>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setMemberPickerOpen(true)}>Đổi</button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setForm({ ...form, userId: '' })}>Bỏ chọn</button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setMemberPickerOpen(true)}>
              + Chọn thành viên từ danh sách
            </button>
          )}
        </div>

        <div className="form-grid">
          <div className="form-group">
              <label>{lang.CONTRIB_CATEGORY || 'Phân loại'}</label>
            <input
              type="text"
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
                placeholder={lang.CONTRIB_CATEGORY_PH || 'VD: Xây dựng website, Phát triển CLB, ...'}
            />
              <p className="form-hint">{lang.CONTRIB_CATEGORY_HINT || 'Phân loại nhóm đóng góp'}</p>
          </div>
          <div className="form-group">
              <label>{lang.CONTRIB_ORDER || 'Thứ tự hiển thị'}</label>
            <input
              type="number"
              value={form.order}
              onChange={e => setForm({ ...form, order: e.target.value })}
            />
              <p className="form-hint">{lang.CONTRIB_ORDER_HINT || 'Số nhỏ hơn hiện trước.'}</p>
          </div>
          <div className="form-group form-group-full">
              <label>{lang.CONTRIB_CONTENT_LABEL || 'Nội dung đóng góp (mỗi dòng một ý)'}</label>
            <textarea
              rows="6"
              value={form.contributions}
              onChange={e => setForm({ ...form, contributions: e.target.value })}
                placeholder={lang.CONTRIB_CONTENT_PH || `Ví dụ:\nThiết kế & phát triển giao diện website\nXây dựng hệ thống online judge\nQuản lý hoạt động CLB giai đoạn 2023-2024`}
            />
          </div>
          <div className="form-group">
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                background: form.highlight ? 'linear-gradient(135deg, rgba(250,204,21,0.18), rgba(249,115,22,0.10))' : 'var(--bg-primary)',
                border: `1px solid ${form.highlight ? '#facc15' : 'var(--border-primary)'}`,
                borderRadius: 8,
                cursor: 'pointer',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-primary)',
                fontSize: '0.9rem',
                margin: 0,
                transition: 'all 0.15s ease'
              }}
            >
              <input
                type="checkbox"
                checked={!!form.highlight}
                onChange={e => setForm({ ...form, highlight: e.target.checked })}
              />
              <span>{form.highlight ? '⭐ Đang đánh dấu nổi bật' : 'Đánh dấu nổi bật'}</span>
            </label>
          </div>
        </div>
        <div className="training-form-actions">
          <button type="submit" className="btn btn-primary btn-sm">{editing ? 'Cập nhật' : 'Thêm'}</button>
          <button type="button" className="btn btn-outline btn-sm" onClick={reset}>{lang.COMMON_CANCEL || 'Huỷ'}</button>
        </div>
      </form>
      )}

      {!editing && !showForm && (
      <>
      <h3 style={{ marginTop: 30 }}>Danh sách hiện tại ({list.length})</h3>
      {loading && <p className="empty-text">Đang tải...</p>}
      {!loading && list.length === 0 && <p className="empty-text">Chưa có đóng góp nào.</p>}
      {!loading && list.length > 0 && (
        <Paginator total={listPg.total} page={listPg.page} pageSize={listPg.pageSize} onChange={listPg.onChange} position="top" />
      )}
      {!loading && list.length > 0 && (
        <table className="training-problem-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Thành viên</th>
              <th>Vai trò</th>
              <th>Phân loại</th>
              <th>Số dòng</th>
              <th>Nổi bật</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
                        {listPg.paged.map(c => (
              <tr key={c.id} style={editing?.id === c.id ? { background: 'rgba(94,234,212,0.06)' } : null}>
                <td>{c.order ?? 0}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {c.avatar
                      ? <img src={`/uploads/profiles/${c.avatar}`} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)' }}>
                          {(c.name || '?').charAt(0).toUpperCase()}
                        </div>}
                    <strong>{c.name}</strong>
                  </div>
                </td>
                <td>{c.title || <span className="training-muted">—</span>}</td>
                <td>{c.category || <span className="training-muted">—</span>}</td>
                <td>{Array.isArray(c.contributions) ? c.contributions.length : 0}</td>
                <td>{c.highlight ? '⭐' : ''}</td>
                <td>
                  <button className="btn btn-outline btn-sm" onClick={() => startEdit(c)}>Sửa</button>
                  <button className="btn btn-danger btn-sm" onClick={() => del(c)} style={{ marginLeft: 6 }}>Xoá</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && list.length > 0 && (
        <Paginator total={listPg.total} page={listPg.page} pageSize={listPg.pageSize} onChange={listPg.onChange} position="bottom" />
      )}
      </>
      )}

      {memberPickerOpen && (
        <div className="contrib-picker-backdrop" onClick={() => setMemberPickerOpen(false)}>
          <div className="contrib-picker-modal" onClick={e => e.stopPropagation()}>
            <div className="contrib-picker-head">
              <h3 style={{ margin: 0 }}>{lang.CONTRIB_SELECT_MEMBER || 'Chọn thành viên'}</h3>
              <button className="btn btn-outline btn-sm" onClick={() => setMemberPickerOpen(false)}>Đóng</button>
            </div>
            <input
              type="search"
              autoFocus
                placeholder={lang.CONTRIB_SEARCH_PH || 'Tìm theo tên, username, email...'}
              value={memberQuery}
              onChange={e => setMemberQuery(e.target.value)}
              style={{ width: '100%', marginBottom: 10 }}
            />
            <div className="contrib-picker-list">
              {filteredMembers.length === 0 && (
                <p className="empty-text" style={{ margin: '20px 0' }}>Không tìm thấy thành viên phù hợp.</p>
              )}
              {filteredMembers.map(m => (
                <button
                  key={m.id}
                  type="button"
                  className="contrib-picker-item"
                  onClick={() => {
                    setForm({ ...form, userId: m.id });
                    setMemberPickerOpen(false);
                    setMemberQuery('');
                  }}
                >
                  {m.avatar
                    ? <img src={`/uploads/profiles/${m.avatar}`} alt="" />
                    : <div className="contrib-picker-item-fallback">
                        {(m.fullName || m.username || '?').charAt(0).toUpperCase()}
                      </div>}
                  <div className="contrib-picker-item-info">
                    <strong>{m.fullName || m.username}</strong>
                    <span>@{m.username} · {describeMember(m)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContributorsAdmin;
